/**
 * 타임스탬프 변환 유틸리티
 *
 * YouTube 스크립트의 타임스탬프 문자열을 초 단위로 변환하거나
 * 초 단위를 타임스탬프 문자열로 포맷합니다.
 */

/**
 * 타임스탬프 문자열을 초 단위로 변환
 *
 * @param timestamp "MM:SS" 또는 "HH:MM:SS" 형식의 문자열
 * @returns 초 단위 숫자, 파싱 실패 시 0
 *
 * @example
 * parseTimestampToSeconds("1:23")    // 83
 * parseTimestampToSeconds("01:23")   // 83
 * parseTimestampToSeconds("1:23:45") // 5025
 */
export function parseTimestampToSeconds(timestamp: string): number {
  if (!timestamp) return 0;

  // "MM:SS" 또는 "HH:MM:SS" 형식 매칭
  const match = timestamp.match(/(\d{1,2}:)?\d{1,2}:\d{1,2}/);
  if (!match) return 0;

  const parts = match[0].split(':').map(Number);
  if (parts.some(Number.isNaN)) return 0;

  // MM:SS 형식
  if (parts.length === 2) {
    const minutes = parts[0] ?? 0;
    const seconds = parts[1] ?? 0;
    return minutes * 60 + seconds;
  }

  // HH:MM:SS 형식
  if (parts.length === 3) {
    const hours = parts[0] ?? 0;
    const minutes = parts[1] ?? 0;
    const seconds = parts[2] ?? 0;
    return hours * 3600 + minutes * 60 + seconds;
  }

  return 0;
}

/**
 * 초 단위를 타임스탬프 문자열로 포맷
 *
 * @param totalSeconds 초 단위 숫자
 * @returns "MM:SS" 또는 "HH:MM:SS" 형식의 문자열
 *
 * @example
 * formatSecondsToTimestamp(83)   // "1:23"
 * formatSecondsToTimestamp(5025) // "1:23:45"
 */
export function formatSecondsToTimestamp(totalSeconds: number): string {
  if (totalSeconds < 0 || !Number.isFinite(totalSeconds)) return '0:00';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
